import { forwardRef, useCallback, useMemo, useState } from 'react';
import type {
  PendingCompaniesListResponse,
  ProjectCompaniesSort,
} from '@/api/v4/projects.api';
import type { Row } from '@/ui/table/table.types';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { styled } from 'goober';
import {
  formatYearOnly,
  CurrencyValue,
} from '@/features/explore/results/cells/company-data-renderer';
import { getAbbreviateNumber } from '@/helpers/format-number';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import { PendingCompanyBaseInfoCell } from './pending-company-base-info-cell';
import { PendingCompanySourceTag } from './pending-company-source-tag';
import { Button } from '@/ui/button/button';
import { useTranslation } from 'react-i18next';
import { useAddPendingCompanyToProject } from '../../pending-companies/use-add-pending-company-to-project.mutation';
import { useSetAtom } from 'jotai';
import { modalState } from '@/state/modal.state';
import { CreateButton } from '@/features/explore/results/explore-create-company-action-button';
import { notify } from '@/ui/snackbar/notify';

const headersConfig = [
  {
    columnKey: 'company',
    sortKey: 'companyName' as const,
    width: '217px',
    padding: '14px',
  },
  {
    columnKey: 'keyOffering',
    width: '160px',
    padding: '14px',
  },
  {
    columnKey: 'source',
    width: '200px',
    padding: '14px',
  },
  {
    columnKey: 'lastFundingDate',
    width: '90px',
    padding: '14px',
  },
  {
    columnKey: 'fundingStage',
    width: '90px',
  },
  {
    columnKey: 'capitalRaised',
    width: '90px',
  },
  {
    hideLabel: true,
    columnKey: 'addToProject',
    width: '140px',
  },
];

export const usePendingProjectsTableConfig = (
  listings: PendingCompaniesListResponse[],
) => {
  const { t } = useTranslation('projects');
  const { mutateAsync: addToProject } = useAddPendingCompanyToProject();
  const setModalState = useSetAtom(modalState);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const headers = useTranslateHeaders<ProjectCompaniesSort>(
    headersConfig,
    'projects',
    'pendingProjects',
  );

  const handleFindAndCreate = useCallback(
    (suggestion: PendingCompaniesListResponse): void => {
      setModalState({
        state: 'createNewCompany',
        defaultSearchPhrase: suggestion.name,
        defaultWebsite: suggestion.website ?? undefined,
      });
    },
    [setModalState],
  );

  const handleAddToProject = useCallback(
    async (suggestion: PendingCompaniesListResponse): Promise<void> => {
      if (
        !suggestion.organizationId &&
        !suggestion.crunchbaseId &&
        suggestion.source !== 'external_form'
      ) {
        handleFindAndCreate(suggestion);
        return;
      }

      setPendingId(suggestion.id);
      try {
        await addToProject({ pendingCompanyId: suggestion.id });
        notify({ message: t`pendingCompanies.added` });
      } catch (error) {
        console.error(error);
        notify({ message: t`pendingCompanies.addError` });
      } finally {
        setPendingId(null);
      }
    },
    [addToProject, handleFindAndCreate, t],
  );

  const rows: Row[] = useMemo<Row[]>(
    () =>
      listings.map(data => {
        // Transform data to match CompanyBaseInfoCell expected format
        const cellData = {
          name: data.name,
          crunchbaseId: data.crunchbaseId || undefined,
          website: data.website || undefined,
          logoUrl: data.logoUrl || undefined,
          organizationId: data.organizationId || undefined,
          hqLocation: data.hqLocation as
            | {
                city: { id: string; name: string } | null;
                region: { id: string; name: string } | null;
                country: { id: string; name: string } | null;
              }
            | null
            | undefined,
          linkedInUrl: data.linkedInUrl || undefined,
        };

        return {
          id: data.id,
          rowHover: true,
          cells: [
            {
              width: '217px',
              padding: '10px',
              value: <PendingCompanyBaseInfoCell data={cellData} />,
            },
            {
              value: (
                <CompanyDataRow width="280px">
                  <EllipsisTextTooltip
                    text={data.keyOffering || '--'}
                    maxWidth="520px"
                    Component={
                      <EllipsisText>{data.keyOffering || '--'}</EllipsisText>
                    }
                  />
                </CompanyDataRow>
              ),
            },
            {
              value: (
                <CompanyDataRow width="140px">
                  <PendingCompanySourceTag source={data.source} />
                </CompanyDataRow>
              ),
            },
            {
              value: data.lastFundingDate ? (
                <CompanyDataRow width="90px">
                  {formatYearOnly(data.lastFundingDate.toString())}
                </CompanyDataRow>
              ) : (
                <CompanyDataRow>--</CompanyDataRow>
              ),
            },
            {
              value: data.lastFundingType ? (
                <CompanyDataRow width="140px">
                  {data.lastFundingType}
                </CompanyDataRow>
              ) : (
                <CompanyDataRow width="140px">--</CompanyDataRow>
              ),
            },
            {
              value: data.fundingTotal ? (
                <CompanyDataRow width="140px">
                  <CurrencyValue>
                    ${getAbbreviateNumber(data.fundingTotal.toString(), 1)}
                  </CurrencyValue>
                </CompanyDataRow>
              ) : (
                <CompanyDataRow width="140px">--</CompanyDataRow>
              ),
            },
            {
              hoverOnly: true,
              value: (
                <CompanyDataRow width="140px">
                  {!data.organizationId &&
                  !data.crunchbaseId &&
                  data.source !== 'external_form' ? (
                    <CreateButton
                      startIcon="Plus"
                      onClick={() => handleFindAndCreate(data)}
                    >{t`findAndCreate`}</CreateButton>
                  ) : (
                    <Button
                      startIcon={pendingId === data.id ? 'Loader' : 'Plus'}
                      onClick={() => handleAddToProject(data)}
                      isLoading={pendingId === data.id}
                      disabled={pendingId !== null}
                    >
                      {t`addCompany.addToProject`}
                    </Button>
                  )}
                </CompanyDataRow>
              ),
            },
          ],
        };
      }),
    [handleAddToProject, handleFindAndCreate, listings, pendingId],
  );

  return {
    headers,
    rows,
  };
};

const CompanyDataRow = styled('div')<{ width?: string }>`
  width: ${({ width }) => width || 'auto'};
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.inter};
  font-size: 14px;
`;

const EllipsisText = styled('p', forwardRef)`
  ${({ theme }) => theme.mixins.lineClamp(2)}
  max-width: 300px;
  white-space: normal;
`;
